// Developer: Ankit Gupta
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { api } from '@/api/client';
import { useAppStore } from '@/store';
import type { Project } from '@/types';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const projectKeys = {
  all: ['projects'] as const,
  list: (params?: Record<string, unknown>) =>
    [...projectKeys.all, 'list', params] as const,
  detail: (id: number | string) =>
    [...projectKeys.all, 'detail', String(id)] as const,
};

// ─── Fetch helpers ────────────────────────────────────────────────────────────

async function fetchProject(id: number | string): Promise<Project> {
  return api.get<Project>(`/projects/${id}`);
}

async function fetchProjects(): Promise<Project[]> {
  // The backend paginates (/projects returns {items, total, page, page_size},
  // 25 per page by default). This hook feeds the project switcher and the
  // active-scope settle logic, both of which need the whole universe: a
  // partial list made useActiveScope reject any project beyond page one and
  // fight ProjectLayout over the selection (React error #185).
  return api.getAll<Project>('/projects');
}

// ─── useProject (single project by route :id) ─────────────────────────────────

export function useProject(idOverride?: number | string) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = idOverride ?? routeId;
  const setCurrentProject = useAppStore((s) => s.setCurrentProject);

  const query = useQuery({
    queryKey: projectKeys.detail(id ?? 'unknown'),
    queryFn: () => fetchProject(id!),
    enabled: id != null,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Keep the global store in sync
  useEffect(() => {
    if (query.data) {
      setCurrentProject(query.data);
    }
  }, [query.data, setCurrentProject]);

  return query;
}

// ─── useProjects (full list) ──────────────────────────────────────────────────

export function useProjects() {
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn: fetchProjects,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// ─── Approval Workflow Mutations ──────────────────────────────────────────────

export function useApproveProject(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comments?: string) =>
      api.post(`/projects/${id}/approve`, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useRejectProject(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comments?: string) =>
      api.post(`/projects/${id}/reject`, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}
